import { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

interface VirtualizedListProps<T> {
  data: T[];
  renderItem: (
    item: T,
    index: number,
    measureRef?: React.RefObject<HTMLDivElement | null>
  ) => React.ReactNode;
  getItemKey: (item: T, index: number) => string | number;
  className?: string;
  itemClassName?: string;
  overscan?: number;
  estimatedItemHeight?: number;
}

export const VirtualizedList = <T,>({
  data,
  renderItem,
  getItemKey,
  className = 'field-ops-customers-content',
  itemClassName,
  overscan = 8,
  estimatedItemHeight = 58,
}: VirtualizedListProps<T>) => {
  const parentRef = useRef<HTMLDivElement | null>(null);
  const itemRef = useRef<HTMLDivElement | null>(null);

  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () =>
      itemRef.current?.getBoundingClientRect().height || estimatedItemHeight,
    overscan,
  });

  return (
    <div ref={parentRef} className={className}>
      <div
        style={{
          height: virtualizer.getTotalSize(),
          width: '100%',
          position: 'relative',
          boxSizing: 'border-box',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const index = virtualRow.index;
          const item = data[index];

          return (
            <div
              key={getItemKey(item, index)}
              ref={virtualizer.measureElement}
              className={itemClassName}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <div
                ref={index === 0 ? itemRef : null}
                style={{
                  padding: '12px 16px',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                {renderItem(item, index, itemRef)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
